import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import Navbar from '../../components/navbar';
import isLoggedIn from '../../utils/isLoggedIn';
import PortfolioTable from '../../components/quantila/portfolio_table';
import { convertEntitiesDeep } from '../../utils/convertEntities';
import styles from '../product.module.css';

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString('en-UK', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '-';

const Supplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [supplier, setSupplier] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { token } = useSelector((state) => state.user);

  const deleteSupplier = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API}/admin/quantila/supplier/${id}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (response.status === 204) {
        toast.success('Supplier was successfully deleted.');
        setTimeout(() => navigate('/quantila/suppliers'), 2000);
      }
    } catch (error) {
      toast.error(
        convertEntitiesDeep(error.response?.data?.error || error.message),
      );
    }
  };

  useEffect(() => {
    isLoggedIn(navigate, dispatch, `/quantila/supplier/${id}`);
    let active = true;

    (async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/admin/quantila/supplier/${id}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (active) {
        setSupplier(convertEntitiesDeep(response.data));
      }
    })();

    return () => {
      active = false;
      setSupplier(null);
    };
  }, [id]);

  return !supplier ? (
    <div className='loading'>
      <img src='/img/loading.gif' alt='Loading' className='loading-img' />
    </div>
  ) : (
    <>
      <Helmet>
        <title>{`Badgers SAS: ${supplier.name}`}</title>
        <meta name='page' content='quantila supplier details' />
      </Helmet>
      <Navbar displayMob={true} />
      <div className={`${styles.background} container`}>
        <Toaster position='top-center' reverseOrder={false} />
        <div>
          <Link className='page-links' to='/'>
            <FontAwesomeIcon icon='home' />{' '}
          </Link>
          <span className='link-span'>
            <FontAwesomeIcon icon='angle-right' />
          </span>
          <Link className='page-links' to='/quantila/suppliers'>
            Suppliers
          </Link>{' '}
          <span className='link-span'>
            <FontAwesomeIcon icon='angle-right' />
          </span>
          <span className='link-span'>{supplier.name}</span>
        </div>
        <div className='col-12 d-flex justify-content-center my-4'>
          <h4 className='h4'>
            SUPPLIER DETAILS <FontAwesomeIcon icon='building' />
          </h4>
        </div>
        <div className={`${styles.card} row g-4 my-3 p-4`}>
          <div className='col-md-6'>
            <h4 className='h5 mb-3'>{supplier.name}</h4>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>SIRET:</span>{' '}
              {supplier.siret || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Address:</span>{' '}
              {supplier.address || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Postcode:</span>{' '}
              {supplier.postcode || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>City:</span> {supplier.city || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Country:</span>{' '}
              {supplier.country || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Website:</span>{' '}
              {supplier.website || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Data Source:</span>{' '}
              {supplier.datasource}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Confidence Score:</span>{' '}
              {supplier.confidenceScore}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Validated:</span>{' '}
              {supplier.validated ? 'Yes' : 'No'}
            </p>
          </div>
          <div className='col-md-6'>
            <h4 className='h5 mb-3'>CONTACT DETAILS</h4>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>DG:</span> {supplier.dg || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Contact:</span>{' '}
              {supplier.contact || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Email:</span>{' '}
              {supplier.email || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>CA:</span> {supplier.ca || '-'}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Created At:</span>{' '}
              {formatDate(supplier.createdAt)}
            </p>
            <p className='product-subheads mb-2'>
              <span className={styles.label}>Updated At:</span>{' '}
              {formatDate(supplier.updatedAt)}
            </p>
          </div>
          <div className='col-12 d-flex flex-wrap gap-2'>
            <button
              className={`${styles.edit} ${styles.btn}`}
              onClick={() => navigate(`/quantila/supplier/update/${id}`)}
            >
              UPDATE SUPPLIER <FontAwesomeIcon icon='pen-square' />
            </button>
            <button
              className={`${styles.list} ${styles.btn}`}
              onClick={() => navigate(`/quantila/supplier/queries/${id}`)}
            >
              SUPPLIER QUERIES <FontAwesomeIcon icon='paper-plane' />
            </button>
            <button
              className={`${styles.del} ${styles.btn}`}
              onClick={() =>
                confirmDelete ? deleteSupplier() : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'DELETE ANYWAY??' : 'DELETE SUPPLIER'}{' '}
              <FontAwesomeIcon icon='trash' />
            </button>
          </div>
        </div>
        <div className='col-12 d-flex justify-content-center my-4'>
          <h4 className='h4'>
            SUPPLIER PORTFOLIO <FontAwesomeIcon icon='building' />
          </h4>
        </div>
        <PortfolioTable items={supplier.supplierPortfolios || []} />
      </div>
    </>
  );
};

export default Supplier;
